const formatDate = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat("en-GB").format(date);
};

const personalise = (value, applicantName, institutionName = "") =>
  String(value || "")
    .replace(/\bAdviser Name\b/gi, applicantName || "the applicant")
    .replace(/\bthe adviser\b/gi, applicantName || "the applicant")
    .replace(/\badviser\b/gi, applicantName || "the applicant")
    .replace(
      /\bEducational Institute\b/gi,
      institutionName || "the educational institution",
    );

const questionLines = (questions, applicantName, institutionName = "") =>
  (questions || [])
    .map(
      (question, index) =>
        `${index + 1}. ${personalise(question, applicantName, institutionName)}`,
    )
    .join("\n");

export const removeReferenceMessageBodySignature = (value) =>
  String(value || "")
    .replace(
      /\s*Thanks\s*(?:and|&)\s*Regards,?\s*(?:\r?\n)+Rosemount Financial Solutions \(IFA\) Ltd\s*$/i,
      "",
    )
    .trim();

export const buildDefaultReferenceMessageBody = ({
  form,
  applicantName,
  nationalInsuranceNumber = "",
  history = {},
}) => {
  const kind = form.referenceKind ||
    (form.referenceType === "educational" ? "educational" : "work-history");
  const supportsQuestions = [
    "personal",
    "accountant",
    "trade",
    "educational",
  ].includes(kind);
  const additionalQuestions = String(
    supportsQuestions ? form.additionalQuestion || "" : "",
  )
    .split(/\r?\n/)
    .map((question) => question.trim())
    .filter(Boolean);
  const questions = [
    ...(supportsQuestions ? form.questions || [] : []),
    ...additionalQuestions,
  ];

  if (kind === "personal") {
    return [
      "I hope this email finds you well.",
      `This is a personal reference for one our recruits, ${applicantName || "the applicant"}.`,
      questions.length
        ? `Please can you answer the questions below honestly and accurately:\n${questionLines(questions, applicantName)}`
        : "",
      "If you have any queries do not hesitate to contact me, I look forward to hearing from you soon.",
    ].filter(Boolean).join("\n\n");
  }

  if (kind === "accountant") {
    const from = formatDate(form.accountantFrom);
    return [
      "I hope this email finds you well.",
      `I have been given your details from ${applicantName || "the applicant"} in regards to a reference${from ? ` covering dates from ${from}` : " covering dates"}.`,
      questions.length
        ? `Therefore, please may I request that the below questions are answered honestly in regards to ${applicantName || "the applicant"}:\n${questionLines(questions, applicantName).replace(/\(Dates\)/gi, from || "(Dates)")}`
        : "",
      "If you have any questions, please do not hesitate to contact me. I look forward to hearing from you soon.",
    ].filter(Boolean).join("\n\n");
  }

  if (kind === "trade") {
    const details = [
      `• Name: ${applicantName || "Applicant"}`,
      form.companyName ? `• Company/Firm: ${form.companyName}` : "",
      form.tradingFrom || form.tradingTo
        ? `• Dates of Trading Activity: ${[form.tradingFrom, form.tradingTo].filter(Boolean).join(" to ")}`
        : "",
    ].filter(Boolean);
    return [
      "I hope this email finds you well.",
      "Please may I request for the attached reference for the above individual to be completed and returned.",
      `Please see below the details for the individual:\n${details.join("\n")}`,
      questions.length
        ? `Please also answer the following questions:\n${questionLines(questions, applicantName)}`
        : "",
      "Please let me know if you have any questions. I look forward to hearing from you.",
    ].filter(Boolean).join("\n\n");
  }

  const isEducational =
    kind === "educational" || form.referenceType === "educational";
  if (isEducational) {
    const institution =
      form.institutionName || history.institutionName || history.nameOfFirm ||
      "your educational institution";
    return [
      "I hope this email finds you well.",
      `I am writing to request a reference for ${applicantName || "the applicant"}, a former student of ${institution}. I would be grateful if you could kindly assist by providing responses to the following questions:`,
      questionLines(questions, applicantName, institution),
      "Thank you for your time and assistance. I look forward to hearing back from you soon.",
    ].filter(Boolean).join("\n\n");
  }

  const role = form.jobTitle || history.currentJobTitle || history.jobTitle || "";
  const currentlyEmployed = [true, "yes", "true"].includes(
    typeof form.currentlyEmployedAtFirm === "string"
      ? form.currentlyEmployedAtFirm.toLowerCase()
      : form.currentlyEmployedAtFirm,
  );
  const details = [
    `• Name: ${applicantName || "Applicant"}`,
    nationalInsuranceNumber
      ? `• National Insurance Number: ${nationalInsuranceNumber}`
      : "",
    form.from || form.to
      ? `• Employment Date: ${formatDate(form.from)} to ${currentlyEmployed ? "Present" : formatDate(form.to)}`
      : "",
    role
      ? `  • He/she stated that they were ${form.employmentType || "employed/self-employed"} as a ${role}.`
      : "",
  ].filter(Boolean);

  return [
    "I hope this email finds you well.",
    "Please may I request for the attached reference for the above individual to be completed and returned.",
    `Please see below the details for the individual:\n${details.join("\n")}`,
    questions.length
      ? `Please also answer the following questions:\n${questionLines(questions, applicantName)}`
      : "",
    "Please let me know if you have any questions. I look forward to hearing from you.",
  ].filter(Boolean).join("\n\n");
};
